import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { propertiesService } from "../services/firebaseServices";
import SEO from "../components/SEO";

const DestinationPage = ({ type }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Content configuration including SEO
  const content = {
    all: {
      title:
        "Luxury Vacation Rentals in Premier Mountain & Coastal Destinations",
      desc: "ACF Luxury Stays offers luxury vacation rentals in carefully selected mountain and beach destinations. We combine refined design with exceptional access to world-class skiing, pristine beaches, and premier dining.",
      filterKey: null,
      showLinks: true,
      seoTitle:
        "Luxury Vacation Rentals in Colorado, California & Florida | ACF Luxury Stays",
      seoDesc:
        "Explore our curated collection of luxury rentals in Telluride, Beaver Creek, San Diego, and Miami Beach. Save on booking fees with direct access to ski-in/ski-out chalets, coastal estates, and oceanfront residences.",
    },
    telluride: {
      title:
        "Luxury Ski-In / Ski-Out Vacation Rentals in Mountain Village, Telluride",
      desc: "Mountain Village sits above historic Telluride and offers unmatched ski-in / ski-out access, gondola connectivity, and alpine elegance. Known for breathtaking San Juan Mountain views and seamless resort access, Mountain Village delivers one of North America’s most refined mountain experiences.",
      filterKey: "Telluride",
      showLinks: false,
      seoTitle:
        "Telluride & Mountain Village Luxury Rentals | ACF Luxury Stays",
      seoDesc:
        "Plan your Telluride retreat in Mountain Village. Our luxury homes offer direct gondola access and panoramic San Sophia views. Book direct for the best rates.",
    },
    beavercreek: {
      title: "Plan Your Next Vacation in Beaver Creek & Avon",
      desc: "Beaver Creek is known for its impeccably groomed terrain and upscale village atmosphere, while Avon provides a vibrant riverside setting with walkable dining and year-round recreation.",
      filterKey: "Avon",
      showLinks: false,
      seoTitle:
        "Beaver Creek & Avon Luxury Vacation Rentals | ACF Luxury Stays",
      seoDesc:
        "Experience Beaver Creek luxury with full Westin Resort access in Avon. Featuring direct gondola connectivity and saltwater pools. No third-party fees.",
    },
    delmar: {
      title: "San Diego Luxury Coastal Retreat | Del Mar, California",
      desc: "Indulge in a secluded sanctuary where sleek modern design meets the timeless charm of the Southern California coast. Just minutes from pristine beaches and the Del Mar Racetrack.",
      filterKey: "San Diego",
      showLinks: false,
      seoTitle: "Del Mar & San Diego Luxury Coastal Rentals | ACF Luxury Stays",
      seoDesc:
        "Retreat to a Del Mar coastal estate with a private infinity pool and swim-up bar. Near San Diego beaches and Torrey Pines. Family-owned luxury.",
    },
    miami: {
      title:
        "Oceanfront Luxury Residences at 1 Hotel South Beach | Miami Beach, Florida",
      desc: "Privately owned residences within South Beach's most iconic oceanfront resort. Wall-to-wall glass frames direct Atlantic Ocean views, while full access to the celebrated amenities of 1 Hotel South Beach places the resort's pools, tropical grounds, and white sand just beyond your door.",
      filterKey: "Miami",
      showLinks: false,
      seoTitle: "Miami Beach & South Beach Luxury Rentals | ACF Luxury Stays",
      seoDesc:
        "Stay in a privately owned residence at 1 Hotel South Beach with direct Atlantic Ocean views and full resort amenity access. Book direct for the best Miami Beach rates.",
    },
  }[type];

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const allProps = await propertiesService.getAll();
        if (content.filterKey) {
          // Flexible filtering
          const filtered = allProps.filter(
            (p) => p.location && p.location.includes(content.filterKey),
          );
          setProperties(filtered);
        } else {
          setProperties(allProps);
        }
      } catch (err) {
        console.error("Failed to load properties", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [type, content.filterKey]);

  return (
    <div className="pt-32 pb-20 max-w-7xl mx-auto px-4">
      <SEO
        title={content.seoTitle}
        description={content.seoDesc}
        url={window.location.pathname}
      />

      <div className="text-center mb-16">
        <h1 className="text-4xl font-primary text-acf-blue mb-6">
          {content.title}
        </h1>
        <div className="w-24 h-1 bg-acf-gold mx-auto mb-8"></div>
        <p className="font-secondary text-gray-600 max-w-3xl mx-auto text-lg">
          {content.desc}
        </p>
      </div>

      {content.showLinks && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          <div className="bg-acf-slate p-8 hover:bg-gray-200 transition group">
            <h3 className="font-primary text-acf-blue text-xl mb-4">
              Telluride | Mountain Village
            </h3>
            <Link
              to="/destinations/mountain-village-telluride"
              className="inline-block border border-acf-blue text-acf-blue px-6 py-2 text-xs uppercase tracking-widest hover:bg-acf-blue hover:text-white transition"
            >
              View Telluride Homes
            </Link>
          </div>
          <div className="bg-acf-slate p-8 hover:bg-gray-200 transition group">
            <h3 className="font-primary text-acf-blue text-xl mb-4">
              Beaver Creek
            </h3>
            <Link
              to="/destinations/avon-beaver-creek"
              className="inline-block border border-acf-blue text-acf-blue px-6 py-2 text-xs uppercase tracking-widest hover:bg-acf-blue hover:text-white transition"
            >
              View Beaver Creek Homes
            </Link>
          </div>
          <div className="bg-acf-slate p-8 hover:bg-gray-200 transition group">
            <h3 className="font-primary text-acf-blue text-xl mb-4">
              San Diego | Del Mar
            </h3>
            <Link
              to="/destinations/del-mar-san-diego"
              className="inline-block border border-acf-blue text-acf-blue px-6 py-2 text-xs uppercase tracking-widest hover:bg-acf-blue hover:text-white transition"
            >
              View San Diego Homes
            </Link>
          </div>
          <div className="bg-acf-slate p-8 hover:bg-gray-200 transition group">
            <h3 className="font-primary text-acf-blue text-xl mb-4">
              Miami Beach | South Beach
            </h3>
            <Link
              to="/destinations/south-beach-miami"
              className="inline-block border border-acf-blue text-acf-blue px-6 py-2 text-xs uppercase tracking-widest hover:bg-acf-blue hover:text-white transition"
            >
              View Miami Beach Homes
            </Link>
          </div>
        </div>
      )}

      {!content.showLinks && (
        <>
          {/* Hardcoded copy for specific destinations as requested */}
          {type === "telluride" && (
            <div className="max-w-4xl mx-auto mb-12 text-center text-gray-600 font-secondary">
              <h2 className="text-2xl font-primary text-acf-blue mb-4">
                Experience the Best of Mountain Village
              </h2>
              <p className="mb-6">
                ACF Luxury Stays offers two luxury vacation homes in Mountain
                Village—both thoughtfully curated for travelers seeking
                slopeside convenience and year-round adventure.
              </p>
              <h2 className="text-2xl font-primary text-acf-blue mb-4">
                Seamless Ski Access
              </h2>
              <p className="mb-6">
                Both homes have exclusive access to ski lockers located right at
                the base of the Village Express lift, or slopeside ski valet at
                The Peaks Resort upon request.
              </p>
            </div>
          )}

          {/* Property Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mt-12">
            {properties.map((property) => (
              <div
                key={property.id}
                className="group flex flex-col bg-white shadow-sm hover:shadow-xl transition-shadow duration-300 rounded-sm overflow-hidden"
              >
                <div className="relative h-64 overflow-hidden">
                  <Link to={`/homes/${property.slug || property.id}`}>
                    <img
                      src={property.image}
                      alt={property.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition duration-700"
                    />
                  </Link>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-primary text-acf-blue mb-2">
                    {property.title}
                  </h3>
                  <div className="text-xs font-bold text-gray-500 mb-4 uppercase tracking-wider">
                    {property.guests} Guests | {property.bedrooms} BR |{" "}
                    {property.baths} BA
                  </div>
                  <Link
                    to={`/homes/${property.slug || property.id}`}
                    className="inline-block text-center border border-acf-blue text-acf-blue py-2 px-6 text-xs font-primary uppercase tracking-widest hover:bg-acf-blue hover:text-white transition duration-300"
                  >
                    Check Availability
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DestinationPage;
